import type HttpClient from "../../tastyworks/http"
import {
  parseSingleResponse,
  type BaseResponse,
  type SingleResponse,
} from "../../tastyworks/response"
import { PASSKEY_DESER, type Passkey } from "../../tastyworks/user"

type PasskeyRegistrationResponseJSON = Omit<
  RegistrationResponseJSON,
  "response"
> & {
  response: Pick<
    AuthenticatorAttestationResponseJSON,
    "attestationObject" | "clientDataJSON"
  > &
    Partial<AuthenticatorAttestationResponseJSON>
}

type PasskeyCreationOptionsJSON = PublicKeyCredentialCreationOptionsJSON & {
  extensions?: { prf?: never; largeBlob?: { write?: never } }
}

export class PasskeyService {
  private readonly passkeysClient: HttpClient

  constructor(userMeClient: HttpClient) {
    this.passkeysClient = userMeClient.nested("passkeys")
  }

  readonly index = async () =>
    this.passkeysClient.indexSimple(PASSKEY_DESER.toParser())

  readonly registrationBegin = async (): Promise<
    SingleResponse<PasskeyCreationOptionsJSON>
  > => {
    const helper = await this.passkeysClient.postJson("registration", {})
    return parseSingleResponse(
      helper,
      (data) => data.toJson() as PasskeyCreationOptionsJSON
    )
  }

  readonly registrationFinish = async (
    credential: PasskeyRegistrationResponseJSON
  ): Promise<SingleResponse<Passkey>> => {
    const helper = await this.passkeysClient.postJson("", credential)
    return parseSingleResponse(helper, PASSKEY_DESER.toParser())
  }

  readonly rename = async (
    id: string,
    name: string
  ): Promise<SingleResponse<Passkey>> => {
    const helper = await this.passkeysClient.patch(
      ":id",
      { name: name.trim() },
      { id }
    )
    return parseSingleResponse(helper, PASSKEY_DESER.toParser())
  }

  readonly destroy = async (id: string): Promise<BaseResponse> =>
    this.passkeysClient.destroy(":id", { id })
}
